import React, { useRef, useEffect, useContext, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import SocialShareButtons from "./SocialShareButtons";
import ClapsButton from "./ClapsButton";
import FollowButton from "./FollowButton";
import CommentSection from "./CommentSection";
import PremiumContentBanner from "./PremiumContentBanner";
import SubscriptionBanner from "./SubscriptionBanner";
import { SubscriptionService } from "../services/SubscriptionService";

export default function ArticleCard({ article, isOpen, onClick }) {
  const { user } = useContext(AuthContext);
  const cardRef = useRef(null);
  const contentRef = useRef(null);
  const titleRef = useRef(null);
  const imageRef = useRef(null);
  const preview = article.summary || (article.contentHtml && article.contentHtml.slice(0, 500)) || (article.content && article.content.slice(0, 500));
  const [hasAccess, setHasAccess] = useState(true);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Check if user has access to premium content
  useEffect(() => {
    const checkAccess = async () => {
      if (isOpen && article.premium) {
        setIsCheckingAccess(true);
        try {
          const access = await SubscriptionService.checkContentAccess(article.id);
          setHasAccess(access);
        } catch (error) {
          console.error("Error checking content access:", error);
          setHasAccess(false);
        } finally {
          setIsCheckingAccess(false);
        }
      }
    };
    
    checkAccess();
  }, [isOpen, article, user]);

  // Handle scrolling within the article
  useEffect(() => {
    if (!isOpen || !contentRef.current) return;
    
    const handleScroll = (e) => {
      // If we're at the bottom or top of the content, prevent further scrolling
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
      const isAtTop = scrollTop <= 0;
      
      if ((isAtBottom && e.deltaY > 0) || (isAtTop && e.deltaY < 0)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    const currentContent = contentRef.current;
    currentContent.addEventListener('wheel', handleScroll, { passive: false });
    
    return () => {
      currentContent.removeEventListener('wheel', handleScroll);
    };
  }, [isOpen]);

  const handleCardClick = (e) => {
    // When card is not open, always toggle on click
    if (!isOpen) {
      onClick();
      return;
    }
    
    // When card is open, only toggle if clicking on title area
    const isClickOnTitle = titleRef.current && (titleRef.current.contains(e.target) || titleRef.current === e.target);
    const isClickOnCardBackground = cardRef.current === e.target;
    const isClickOnContentArea = contentRef.current && contentRef.current.contains(e.target);
    
    if (isOpen && (isClickOnTitle || isClickOnCardBackground) && !isClickOnContentArea) {
      onClick();
    }
  };

  // Determine if we should show premium content banner
  const showPremiumBanner = isOpen && article.premium && !hasAccess && !isCheckingAccess;
  
  // Determine if we should show full content
  const showFullContent = isOpen && (!article.premium || hasAccess || isCheckingAccess);

  return (
    <div
      ref={cardRef}
      onClick={handleCardClick}
      className={`group relative w-full max-w-4xl bg-white rounded-lg shadow-sm cursor-pointer transition
                  ${isOpen ? "p-8" : "p-6 min-h-[66vh]"}`}
      aria-expanded={isOpen}
    >
      <div ref={titleRef} className="cursor-pointer">
        <h3 className="text-3xl font-serif text-taupe-dark">{article.title}</h3>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            By {article.author || "Unknown Author"}
            {article.publishDate && (
              <span className="ml-2 text-xs">• {new Date(article.publishDate).toLocaleDateString()}</span>
            )}
          </p>
          
          {/* Premium indicator */}
          {article.premium && (
            <span className="px-2 py-0.5 bg-taupe/10 text-taupe-dark text-xs rounded-full">
              Premium
            </span>
          )}
        </div>
        
        {isOpen && (
          <div className="mt-2 text-xs text-taupe hover:text-taupe-dark">
            Click here to collapse
          </div>
        )}
      </div>

      {/* Display categories if available */}
      {article.categories && article.categories.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {article.categories.map(category => (
            <span 
              key={category} 
              className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
            >
              {category}
            </span>
          ))}
        </div>
      )}

      {/* Featured image */}
      {article.imageUrl && !isOpen && (
        <div className="mt-4 w-full h-48 overflow-hidden rounded-md">
          <img
            ref={imageRef}
            src={article.imageUrl}
            alt={article.imageAlt || `Image for ${article.title}`}
            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
          />
        </div>
      )}

      {/* Preview content when card is closed */}
      {!isOpen && preview && (
        <p className="mt-4 font-sans text-gray-700 line-clamp-3">{preview}</p>
      )}

      {/* Full content when card is open */}
      {showFullContent && (
        <div 
          ref={contentRef}
          className="prose mt-6 max-w-none overflow-y-auto max-h-[calc(100vh-16rem)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Featured image when article is open */}
          {article.imageUrl && isOpen && (
            <div className="mb-6 w-full max-h-96 overflow-hidden rounded-md">
              <img
                src={article.imageUrl}
                alt={article.imageAlt || `Image for ${article.title}`}
                className="w-full object-cover"
                loading="lazy"
              />
              {article.imageCaption && (
                <p className="text-sm text-gray-500 mt-1 italic">{article.imageCaption}</p>
              )}
            </div>
          )}
          
          {article.contentHtml ? (
            <div dangerouslySetInnerHTML={{ __html: article.contentHtml }} />
          ) : (
            article.content || article.summary
          )}
          
          {/* Social interaction components */}
          {isOpen && (
            <div className="mt-8 pt-4 border-t">
              <div className="flex items-center justify-between mb-6">
                <ClapsButton articleId={article.id} />
                <SocialShareButtons article={article} />
              </div>
              
              <div className="flex items-center justify-between mb-8">
                <FollowButton authorUsername={article.author} />
              </div>
              
              <SubscriptionBanner 
                authorUsername={article.author} 
                authorName={article.author} 
                className="mb-8"
              />
              
              <CommentSection articleId={article.id} />
            </div>
          )}
        </div>
      )}
      
      {/* Premium content banner */}
      {showPremiumBanner && (
        <PremiumContentBanner authorName={article.author} className="mt-6" />
      )}

      {/* "Read more" overlay when card is closed */}
      {!isOpen && (
        <div className="absolute inset-x-0 bottom-0 bg-taupe/70 text-white
                        text-center py-2 opacity-0 group-hover:opacity-100
                        transition rounded-b-lg">
          <span className="flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            Click to read more
          </span>
        </div>
      )}
      
      {/* Reading time indicator */}
      {article.readingTime && (
        <div className="absolute top-4 right-4 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
          {article.readingTime} min read
        </div>
      )}
    </div>
  );
}